import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { baseURL, requestMiddleware, headers } from '@/v2/constants/requests'
import { nowOnClinicTimezone } from '../utils/convert'

export const useAppointment = ({ appointmentId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['Appointment', `${appointmentId}`],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/appointments/${appointmentId}`
      )
      if (!res.ok) {
        throw new Error("We couldn't find the appointment")
      }
      const result = await res.json()
      return result
    },
    enabled: !!appointmentId,
  })

  return {
    appointment: data,
    isLoading,
    isError,
  }
}

export const useCreateAppointment = () => {
  const queryClient = useQueryClient()
  const { mutateAsync: createAppointment, ...others } = requestMiddleware({
    key: ['CreateAppointment'],
    request: async appointmentData =>
      await fetch(`${baseURL}/v2/rest/appointments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ appointment: appointmentData }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['NextAppointments'] })
      queryClient.invalidateQueries({ queryKey: ['DailyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['WeeklyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['PatientAppointments'] })
    },
  })

  return { createAppointment, ...others }
}

export const useUpdateAppointment = ({ appointmentId }) => {
  const queryClient = useQueryClient()

  const trackEvent = (eventName, params) => {
    const clientId =
      queryClient.getQueryData(['Appointment', `${appointmentId}`])?.patient
        ?.googleClientId || 'no_client_id'
    console.log('Tracking Event:', { ...params, clientId })
    if (
      !queryClient.getQueryData(['Appointment', `${appointmentId}`])?.patient
        ?.googleClientId
    )
      return
    if (window.analytics && window.analytics.track) {
      window.analytics.track(eventName, { ...params, clientId })
    }
  }

  const { mutateAsync: updateAppointment, ...others } = requestMiddleware({
    key: ['UpdateAppointment', `${appointmentId}`],
    request: async appointmentData => {
      return await fetch(
        `${baseURL}/v2/rest/appointments/${appointmentId ||
          appointmentData.id}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ appointment: appointmentData }),
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['Appointment', `${appointmentId}`],
      })
      queryClient.invalidateQueries({ queryKey: ['NextAppointments'] })
      queryClient.invalidateQueries({ queryKey: ['DailyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['WeeklyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['PatientAppointments'] })
    },
  })

  // Optimistically reflect the status change in the cached appointment so the
  // button flips instantly instead of waiting for the PUT + refetch round-trip.
  // The onSuccess invalidation above reconciles with the server; roll back if
  // the request fails (an HTTP error resolves as { error }; a network error
  // throws).
  const runUpdate = async appointmentData => {
    const key = ['Appointment', `${appointmentId || appointmentData.id}`]
    const previous = queryClient.getQueryData(key)
    if (previous) {
      queryClient.setQueryData(key, { ...previous, ...appointmentData })
    }
    try {
      const result = await updateAppointment(appointmentData)
      if (result?.error && previous) queryClient.setQueryData(key, previous)
      return result
    } catch (e) {
      if (previous) queryClient.setQueryData(key, previous)
      throw e
    }
  }

  const bookingCheckIn = async (value = true, id) => {
    const result = await runUpdate({
      id: appointmentId || id,
      checkedIn: value,
      checkedInTime: value
        ? format(nowOnClinicTimezone(), 'yyyy-M-d hh:mm aa')
        : null,
    })
    if (!result?.error) {
      trackEvent('Appointment Checked In', {
        category: 'Clearworks',
        label: 'Appointment checked in by staff',
        status: value ? 'checked_in' : 'unchecked',
        booking_id: appointmentId || id,
      })
    }
    return result
  }

  const seatPatient = async values => {
    const result = await runUpdate({
      id: appointmentId || values.id,
      doctorId: values.doctorId,
      roomId: values.roomId,
      seated: values.seated,
      seatedTime: values.seated
        ? format(nowOnClinicTimezone(), 'yyyy-M-d hh:mm aa')
        : null,
    })
    if (!result?.error) {
      trackEvent('Patient Seated', {
        category: 'Clearworks',
        label: 'Patient seated by staff',
        status: values.seated ? 'seated' : 'not_seated',
        booking_id: appointmentId || values.id,
        seated_time: values.seated,
      })
    }
    return result
  }

  const confirmPatient = async value => {
    const result = await runUpdate({ id: appointmentId, completed: value })
    if (!result?.error) {
      trackEvent('Appointment Confirmed', {
        category: 'Clearworks',
        label: 'Appointment confirmed by staff',
        status: value ? 'completed' : 'not_completed',
        booking_id: appointmentId,
      })
    }
    return result
  }

  const toggleReschedule = async value => {
    const result = await runUpdate({ id: appointmentId, reschedule: value })
    if (!result?.error) {
      trackEvent('Appointment Rescheduled', {
        category: 'Clearworks',
        label: 'Appointment rescheduled by staff',
        status: value ? 'rescheduled' : 'not_rescheduled',
        booking_id: appointmentId,
      })
    }
    return result
  }

  const toggleNoShow = async value => {
    const result = await runUpdate({ id: appointmentId, noShow: value })
    if (!result?.error) {
      trackEvent('Patient no-show', {
        category: 'Clearworks',
        label: 'Patient no-show by staff',
        status: value ? 'no_show' : 'not_no_show',
        booking_id: appointmentId,
      })
    }
    return result
  }

  return {
    updateAppointment,
    bookingCheckIn,
    confirmPatient,
    seatPatient,
    toggleReschedule,
    toggleNoShow,
    ...others,
  }
}

export const useDeleteAppointment = ({ appointmentId }) => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteAppointment, ...others } = requestMiddleware({
    key: ['DeleteAppointment', `${appointmentId}`],
    request: async () =>
      await fetch(`${baseURL}/v2/rest/appointments/${appointmentId}`, {
        method: 'DELETE',
        headers,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['NextAppointments'] })
      queryClient.invalidateQueries({ queryKey: ['DailyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['WeeklyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['PatientAppointments'] })
    },
  })

  return {
    deleteAppointment,
    ...others,
  }
}

export const useCombinedAppointment = appointmentId => {
  const { appointment, isLoading, isError } = useAppointment(appointmentId)
  const {
    bookingCheckIn,
    confirmPatient,
    seatPatient,
    toggleReschedule,
    toggleNoShow,
  } = useUpdateAppointment(appointmentId)

  return {
    appointment,
    loading: isLoading,
    error: isError,
    bookingCheckIn,
    confirmPatient,
    seatPatient,
    toggleReschedule,
    toggleNoShow,
  }
}

export default useCombinedAppointment
